// Particle effects (instanced voxel cubes), shockwave rings, lightning beams,
// and DOM overlays anchored to 3D positions (floating text, speech bubbles).
import * as THREE from 'three';
import { BLOCKS } from './blocks.js';

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _s = new THREE.Vector3();
const _p = new THREE.Vector3();
const _c = new THREE.Color();

class Pool {
  constructor(scene, material, max) {
    this.max = max;
    this.mesh = new THREE.InstancedMesh(new THREE.BoxGeometry(1, 1, 1), material, max);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.setColorAt(0, _c.set(1, 1, 1));
    this.mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.items = [];
    scene.add(this.mesh);
  }
}

export class Particles {
  constructor(scene, max = 1400) {
    this.lit = new Pool(scene, new THREE.MeshLambertMaterial({ color: 0xffffff }), max);
    this.lit.mesh.castShadow = false;
    this.glow = new Pool(scene, new THREE.MeshBasicMaterial({ color: 0xffffff }), max);
  }

  spawn(o) {
    const pool = o.glow ? this.glow : this.lit;
    if (pool.items.length >= pool.max) return;
    pool.items.push({
      x: o.x, y: o.y, z: o.z,
      vx: o.vx || 0, vy: o.vy || 0, vz: o.vz || 0,
      life: o.life || 1, max: o.life || 1,
      size: o.size || 0.15,
      sx: o.sx || 1, sy: o.sy || 1, sz: o.sz || 1,
      rx: Math.random() * 6, ry: Math.random() * 6,
      vr: o.spin ?? (Math.random() - 0.5) * 8,
      g: o.gravity ?? 14,
      drag: o.drag ?? 0.5,
      c: new THREE.Color(o.color || '#ffffff').multiplyScalar(o.bright || 1),
      shrink: o.shrink ?? true,
      floor: o.floor ?? -Infinity,
    });
  }

  burst(pos, o = {}) {
    const n = o.count ?? 12;
    const cols = o.colors || [o.color || '#ffffff'];
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = (o.speed ?? 4) * (0.4 + Math.random() * 0.8);
      const sz = (o.size ?? 0.15) * (0.6 + Math.random() * 0.8);
      this.spawn({
        x: pos.x + (Math.random() - 0.5) * (o.spread ?? 0.4),
        y: pos.y + (Math.random() - 0.5) * (o.spread ?? 0.4),
        z: pos.z + (Math.random() - 0.5) * (o.spread ?? 0.4),
        vx: Math.cos(a) * sp,
        vz: Math.sin(a) * sp,
        vy: (o.up ?? 3) * (0.5 + Math.random()),
        life: (o.life ?? 0.8) * (0.7 + Math.random() * 0.6),
        size: sz,
        color: cols[Math.floor(Math.random() * cols.length)],
        gravity: o.gravity ?? 14,
        glow: o.glow,
        bright: o.bright,
        drag: o.drag,
        sx: o.sx, sy: o.sy, sz: o.sz,
        spin: o.spin,
      });
    }
  }

  blockBreak(pos, id) {
    const b = BLOCKS[id];
    const base = new THREE.Color(b ? b.color : '#888');
    const cols = [base.clone(), base.clone().multiplyScalar(0.8), base.clone().lerp(new THREE.Color('#fff'), 0.2)];
    this.burst(pos, { count: 16, colors: cols, speed: 4, up: 5, size: 0.16, life: 0.9, gravity: 20, spread: 0.7 });
  }

  sparkle(pos, color = '#fff6b0', n = 6) {
    for (let i = 0; i < n; i++)
      this.spawn({
        x: pos.x + (Math.random() - 0.5) * 0.8,
        y: pos.y + (Math.random() - 0.5) * 0.8,
        z: pos.z + (Math.random() - 0.5) * 0.8,
        vy: 0.8 + Math.random(),
        life: 0.5 + Math.random() * 0.4,
        size: 0.07 + Math.random() * 0.06,
        color,
        gravity: 0,
        glow: true,
        bright: 2.2,
      });
  }

  confetti(pos, n = 60, spread = 2) {
    const cols = ['#ff4d6d', '#ffd23f', '#3bceac', '#4d9de0', '#b15eff', '#ff8c42', '#ffffff'];
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 2 + Math.random() * 5;
      this.spawn({
        x: pos.x + (Math.random() - 0.5) * spread,
        y: pos.y,
        z: pos.z + (Math.random() - 0.5) * spread,
        vx: Math.cos(a) * sp,
        vz: Math.sin(a) * sp,
        vy: 6 + Math.random() * 7,
        life: 2 + Math.random() * 1.5,
        size: 0.14,
        sx: 1.4, sy: 0.15, sz: 1,
        color: cols[i % cols.length],
        gravity: 6,
        drag: 2.2,
        spin: (Math.random() - 0.5) * 14,
        glow: true,
        bright: 1.2,
        shrink: false,
      });
    }
  }

  firework(pos, color) {
    const c = color || ['#ff4d6d', '#ffd23f', '#3bceac', '#4d9de0', '#b15eff'][Math.floor(Math.random() * 5)];
    for (let i = 0; i < 46; i++) {
      const u = Math.random() * 2 - 1, th = Math.random() * Math.PI * 2;
      const r = Math.sqrt(1 - u * u);
      const sp = 7 + Math.random() * 2;
      this.spawn({
        x: pos.x, y: pos.y, z: pos.z,
        vx: r * Math.cos(th) * sp, vy: u * sp, vz: r * Math.sin(th) * sp,
        life: 1.1 + Math.random() * 0.5,
        size: 0.13,
        color: i % 6 === 0 ? '#ffffff' : c,
        gravity: 4,
        drag: 2,
        glow: true,
        bright: 2.5,
      });
    }
  }

  dust(pos, n = 6, color = '#d8cbb0') {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      this.spawn({
        x: pos.x + Math.cos(a) * 0.3,
        y: pos.y + 0.05,
        z: pos.z + Math.sin(a) * 0.3,
        vx: Math.cos(a) * 1.5,
        vz: Math.sin(a) * 1.5,
        vy: 0.6 + Math.random() * 0.8,
        life: 0.45 + Math.random() * 0.25,
        size: 0.12 + Math.random() * 0.08,
        color,
        gravity: 1,
        drag: 3,
      });
    }
  }

  trail(pos, type, t) {
    switch (type) {
      case 'sparkle':
        this.spawn({ x: pos.x + (Math.random() - 0.5) * 0.4, y: pos.y + 0.3 + Math.random() * 0.8, z: pos.z + (Math.random() - 0.5) * 0.4, vy: 0.3, life: 0.6, size: 0.08, color: Math.random() < 0.5 ? '#fff6b0' : '#ffffff', gravity: 0, glow: true, bright: 2 });
        break;
      case 'fire':
        this.spawn({ x: pos.x + (Math.random() - 0.5) * 0.4, y: pos.y + 0.15, z: pos.z + (Math.random() - 0.5) * 0.4, vy: 1.6 + Math.random(), life: 0.5, size: 0.16, color: ['#ff5a1f', '#ffb020', '#ffe070'][Math.floor(Math.random() * 3)], gravity: -1, glow: true, bright: 2 });
        break;
      case 'rainbow': {
        const h = (t * 0.6) % 1;
        this.spawn({ x: pos.x, y: pos.y + 0.6 + Math.random() * 0.6, z: pos.z, life: 0.7, size: 0.18, color: new THREE.Color().setHSL(h, 1, 0.55).getHexString().padStart(7, '#'), gravity: 0, glow: true, bright: 1.8, spin: 0 });
        break;
      }
      case 'diamond':
        this.spawn({ x: pos.x + (Math.random() - 0.5) * 0.5, y: pos.y + 0.2 + Math.random(), z: pos.z + (Math.random() - 0.5) * 0.5, vy: 0.4, life: 0.8, size: 0.1, color: Math.random() < 0.5 ? '#5ff5f0' : '#bffffb', gravity: 2, glow: true, bright: 2 });
        break;
      case 'lightning':
        this.spawn({ x: pos.x + (Math.random() - 0.5) * 0.7, y: pos.y + Math.random() * 1.6, z: pos.z + (Math.random() - 0.5) * 0.7, life: 0.25, size: 0.09, sx: 0.4, sy: 3, sz: 0.4, color: Math.random() < 0.7 ? '#fff27a' : '#ffffff', gravity: 0, glow: true, bright: 3 });
        break;
      case 'notes':
        this.spawn({ x: pos.x + (Math.random() - 0.5) * 0.5, y: pos.y + 1 + Math.random() * 0.6, z: pos.z + (Math.random() - 0.5) * 0.5, vy: 0.8, life: 0.9, size: 0.12, sx: 1, sy: 1.6, sz: 0.3, color: ['#ff6fb5', '#6fd3ff', '#ffe36f'][Math.floor(Math.random() * 3)], gravity: 0, glow: true, bright: 1.8, spin: 0 });
        break;
      case 'glitch':
        this.spawn({ x: pos.x + (Math.random() - 0.5) * 0.8, y: pos.y + Math.random() * 1.8, z: pos.z + (Math.random() - 0.5) * 0.8, life: 0.35, size: 0.12, color: Math.random() < 0.5 ? '#ff2bd6' : '#22e8ff', gravity: 0, glow: true, bright: 2.2, spin: 0 });
        break;
      case 'hearts':
        this.spawn({ x: pos.x + (Math.random() - 0.5) * 0.5, y: pos.y + 0.8, z: pos.z + (Math.random() - 0.5) * 0.5, vy: 1, life: 0.8, size: 0.14, color: '#ff4d7a', gravity: 0, glow: true, bright: 1.8 });
        break;
      default:
        break;
    }
  }

  update(dt) {
    for (const pool of [this.lit, this.glow]) {
      const items = pool.items;
      let n = 0;
      for (let i = 0; i < items.length; i++) {
        const p = items[i];
        p.life -= dt;
        if (p.life <= 0) continue;
        p.vy -= p.g * dt;
        const d = Math.exp(-p.drag * dt);
        p.vx *= d;
        p.vz *= d;
        if (p.drag > 1) p.vy *= Math.exp(-(p.drag - 1) * 0.5 * dt);
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.z += p.vz * dt;
        if (p.y < p.floor) {
          p.y = p.floor;
          p.vy *= -0.3;
        }
        p.rx += p.vr * dt;
        p.ry += p.vr * 0.7 * dt;
        const k = p.shrink ? Math.min(1, (p.life / p.max) * 1.6) : Math.min(1, p.life * 3);
        const s = p.size * k;
        _e.set(p.rx, p.ry, 0);
        _q.setFromEuler(_e);
        _s.set(s * p.sx, s * p.sy, s * p.sz);
        _p.set(p.x, p.y, p.z);
        _m.compose(_p, _q, _s);
        pool.mesh.setMatrixAt(n, _m);
        pool.mesh.setColorAt(n, p.c);
        items[n++] = p;
      }
      items.length = n;
      pool.mesh.count = n;
      pool.mesh.instanceMatrix.needsUpdate = true;
      if (pool.mesh.instanceColor) pool.mesh.instanceColor.needsUpdate = true;
    }
  }

  clear() {
    this.lit.items.length = 0;
    this.glow.items.length = 0;
    this.lit.mesh.count = 0;
    this.glow.mesh.count = 0;
  }
}

// Expanding ground rings (shockwaves, landing marks)
export class Rings {
  constructor(scene) {
    this.scene = scene;
    this.list = [];
    this.geo = new THREE.RingGeometry(0.85, 1, 48);
    this.geo.rotateX(-Math.PI / 2);
  }
  add(pos, { color = '#ffffff', from = 0.5, to = 4, life = 0.6, opacity = 0.9, bright = 1.5 } = {}) {
    const mat = new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(bright), transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide });
    const m = new THREE.Mesh(this.geo, mat);
    m.position.copy(pos);
    m.position.y += 0.05;
    this.scene.add(m);
    this.list.push({ m, t: 0, life, from, to, opacity });
    return m;
  }
  update(dt) {
    for (let i = this.list.length - 1; i >= 0; i--) {
      const r = this.list[i];
      r.t += dt;
      const k = r.t / r.life;
      if (k >= 1) {
        this.scene.remove(r.m);
        r.m.material.dispose();
        this.list.splice(i, 1);
        continue;
      }
      const s = r.from + (r.to - r.from) * (1 - Math.pow(1 - k, 2));
      r.m.scale.set(s, 1, s);
      r.m.material.opacity = r.opacity * (1 - k);
    }
  }
  clear() {
    for (const r of this.list) {
      this.scene.remove(r.m);
      r.m.material.dispose();
    }
    this.list = [];
  }
}

// Lightning beam between two points
export class Beams {
  constructor(scene) {
    this.scene = scene;
    this.list = [];
  }
  add(from, to, { color = '#fff27a', life = 0.45, radius = 0.12 } = {}) {
    const pts = [];
    const segs = 12;
    for (let i = 0; i <= segs; i++) {
      const t = i / segs;
      const p = new THREE.Vector3().lerpVectors(from, to, t);
      if (i > 0 && i < segs) p.add(new THREE.Vector3((Math.random() - 0.5) * 0.9, (Math.random() - 0.5) * 0.9, (Math.random() - 0.5) * 0.9));
      pts.push(p);
    }
    const curve = new THREE.CatmullRomCurve3(pts);
    const geo = new THREE.TubeGeometry(curve, 48, radius, 5, false);
    const mat = new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(3), transparent: true, opacity: 1 });
    const m = new THREE.Mesh(geo, mat);
    this.scene.add(m);
    this.list.push({ m, t: 0, life });
  }
  update(dt) {
    for (let i = this.list.length - 1; i >= 0; i--) {
      const b = this.list[i];
      b.t += dt;
      const k = b.t / b.life;
      if (k >= 1) {
        this.scene.remove(b.m);
        b.m.geometry.dispose();
        b.m.material.dispose();
        this.list.splice(i, 1);
        continue;
      }
      b.m.material.opacity = (1 - k) * (0.6 + 0.4 * Math.random());
    }
  }
  clear() {
    for (const b of this.list) {
      this.scene.remove(b.m);
      b.m.geometry.dispose();
      b.m.material.dispose();
    }
    this.list = [];
  }
}

// DOM elements anchored to world positions
const _v = new THREE.Vector3();
export class WorldUI {
  constructor(root, camera) {
    this.root = root;
    this.camera = camera;
    this.floats = [];
    this.anchors = new Map();
  }
  project(pos) {
    _v.copy(pos).project(this.camera);
    const w = this.root.clientWidth, h = this.root.clientHeight;
    return { x: (_v.x * 0.5 + 0.5) * w, y: (-_v.y * 0.5 + 0.5) * h, visible: _v.z < 1 && _v.z > -1 };
  }
  float(text, pos, cls = '', { life = 1.1, rise = 70 } = {}) {
    if (this.floats.length > 40) return;
    const el = document.createElement('div');
    el.className = 'float-text ' + cls;
    el.innerHTML = text;
    this.root.appendChild(el);
    this.floats.push({ el, pos: pos.clone(), t: 0, life, rise });
  }
  // persistent anchored element (speech bubble / prompt)
  anchor(id, html, pos, cls = 'bubble') {
    let a = this.anchors.get(id);
    if (!a) {
      const el = document.createElement('div');
      el.className = cls;
      this.root.appendChild(el);
      a = { el, html: null, pos: new THREE.Vector3(), shown: false };
      this.anchors.set(id, a);
    }
    if (a.html !== html) {
      // the outer element is positioned every frame; animations live on the inner box
      a.el.innerHTML = `<div class="anchor-box">${html}</div>`;
      a.html = html;
    }
    a.pos.copy(pos);
    a.shown = true;
    a.el.classList.add('show');
    return a;
  }
  hide(id) {
    const a = this.anchors.get(id);
    if (a && a.shown) {
      a.shown = false;
      a.el.classList.remove('show');
    }
  }
  update(dt) {
    for (let i = this.floats.length - 1; i >= 0; i--) {
      const f = this.floats[i];
      f.t += dt;
      const k = f.t / f.life;
      if (k >= 1) {
        f.el.remove();
        this.floats.splice(i, 1);
        continue;
      }
      const p = this.project(f.pos);
      const y = p.y - f.rise * (1 - Math.pow(1 - k, 2));
      const sc = k < 0.15 ? 0.6 + (k / 0.15) * 0.5 : k < 0.25 ? 1.1 - ((k - 0.15) / 0.1) * 0.1 : 1;
      f.el.style.transform = `translate(${p.x}px, ${y}px) translate(-50%, -50%) scale(${sc})`;
      f.el.style.opacity = p.visible ? (k > 0.7 ? 1 - (k - 0.7) / 0.3 : 1) : 0;
    }
    const W = this.root.clientWidth, H = this.root.clientHeight;
    for (const a of this.anchors.values()) {
      if (!a.shown) continue;
      const p = this.project(a.pos);
      // keep bubbles fully on screen
      const w = a.el.offsetWidth || 200, h = a.el.offsetHeight || 60;
      const x = Math.max(w / 2 + 8, Math.min(W - w / 2 - 8, p.x));
      const y = Math.max(h + 70, Math.min(H - 8, p.y));
      a.el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -100%)`;
      a.el.style.visibility = p.visible ? 'visible' : 'hidden';
    }
  }
  clear() {
    for (const f of this.floats) f.el.remove();
    this.floats = [];
    for (const a of this.anchors.values()) a.el.remove();
    this.anchors.clear();
  }
}
